import {useAnalytics} from '@github-ui/use-analytics'

import {useEffect, useState} from 'react'
import {clsx} from 'clsx'

import {
  type DirectMergeActionsSectionProps,
  DirectMergeActionsSection,
  type MergeButtonFocusProps,
} from './DirectMergeActionsSection'
import type {
  MergeStateStatus,
  ViewerMergeActions,
  MergeQueue,
  AutoMergeRequest,
  PullRequestMergeAction,
  PullRequestMergeRequirementsState,
  AdvisoryWorkspace,
} from '../../../types'
import {BypassMergeRequirementsToggle} from './BypassMergeRequirementsToggle'
import type {
  PullRequestMergeRequirementsPayload,
  JSONAPIPullRequestPayload,
} from '../../../page-data/payloads/merge-box'
import type {Status} from '../../../helpers/mergeability-status'
import type {MergeBoxRollupStatus} from '../../../helpers/merge-box-status-calculator/types'
import {useIsMergeBoxSidePanel} from '../../../contexts/MergeBoxLayoutContext'
import {useIsFeatureStateEnabled} from '@github-ui/pull-requests/use-feature-state'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'
import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-types'
import {DisableAutoMerge} from './DisableAutoMerge'
import {AddToMergeQueueSection} from './AddToMergeQueueSection'
import {deriveMergeControlModel} from './derive-merge-control-model'

export type MergeSectionProps = {
  advisoryWorkspace: AdvisoryWorkspace
  autoMergeRequest: AutoMergeRequest
  baseRefName: string
  canUserPushToBase: boolean
  defaultCommitAuthorEmail: PullRequestMergeRequirementsPayload['defaultCommitAuthorEmail']
  commitMessageBody: PullRequestMergeRequirementsPayload['commitMessageBody']
  commitMessageHeadline: PullRequestMergeRequirementsPayload['commitMessageHeadline']
  conflictsCondition: DirectMergeActionsSectionProps['conflictsCondition']
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  mergeCurrentlyBlockedForStacks?: boolean
  headRepository: {ownerLogin: string; name: string} | null
  helpUrl: string
  id: string
  isDraft: boolean
  isConfirmingMergeInfo: boolean
  isCrossRepo: boolean
  isInMergeQueue: boolean
  isStackMergeInProgress?: boolean
  isStacked?: boolean
  stackedEntriesToMergeCount?: number
  stackedEntries?: StackSectionStackEntry[]
  stackMetadata?: PullRequestUserActionStack
  mergeBoxRollupStatus: MergeBoxRollupStatus
  mergeQueue: MergeQueue
  mergeRequirementsState: PullRequestMergeRequirementsState
  mergeStateStatus: MergeStateStatus
  numberOfCommits: number
  onStackMergePendingChange?: (pendingAction?: PullRequestMergeAction) => void
  status: Status
  viewerCanAddAndRemoveFromMergeQueue: JSONAPIPullRequestPayload['viewerCanAddAndRemoveFromMergeQueue']
  viewerCanAddToMergeQueueSolo: JSONAPIPullRequestPayload['viewerCanAddToMergeQueueSolo']
  viewerCanAdminBypassMergeRequirements: boolean
  viewerCanDisableAutoMerge: JSONAPIPullRequestPayload['viewerCanDisableAutoMerge']
  viewerCanEnableAutoMerge: JSONAPIPullRequestPayload['viewerCanEnableAutoMerge']
  viewerMergeActions: ViewerMergeActions
  possibleCommitAuthorEmails: PullRequestMergeRequirementsPayload['possibleCommitAuthorEmails']
} & MergeButtonFocusProps

function assertNeverControl(control: never): never {
  throw new Error(`Unexpected merge control: ${JSON.stringify(control)}`)
}

/**
 * Renders the merge actions section of the merge box, specifically the merge button
 * Depending on configurations, a viewer can select a merge method, merge directly, or add the pull request to the merge queue
 */
export function MergeSection({
  advisoryWorkspace,
  autoMergeRequest,
  handleConfirmingMergeInfo,
  mergeCurrentlyBlockedForStacks,
  id,
  isConfirmingMergeInfo,
  isInMergeQueue,
  isStackMergeInProgress = false,
  mergeQueue,
  mergeStateStatus,
  onStackMergePendingChange,
  viewerCanAddAndRemoveFromMergeQueue,
  viewerCanAddToMergeQueueSolo,
  viewerCanDisableAutoMerge,
  viewerCanEnableAutoMerge,
  viewerCanAdminBypassMergeRequirements,
  isStacked,
  stackedEntries,
  stackMetadata,
  ...rest
}: MergeSectionProps & MergeButtonFocusProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const isSidePanel = useIsMergeBoxSidePanel()
  const [isAdminBypassToggleChecked, setIsAdminBypassToggleChecked] = useState(false)
  const isStackedPrAdminBypassEnabled = useIsFeatureStateEnabled('stackedPrAdminBypass')
  const isPullRequestStacksAsyncMergeEnabled = useIsFeatureStateEnabled('pullRequestStacksAsyncMerge')

  const mergeControlModel = deriveMergeControlModel({
    advisoryWorkspace,
    autoMergeRequest,
    isAdminBypassToggleChecked,
    isConfirmingMergeInfo,
    isPullRequestStacksAsyncMergeEnabled,
    isStackMergeInProgress,
    isStacked,
    isStackedPrAdminBypassEnabled,
    mergeRequirementsState: rest.mergeRequirementsState,
    mergeStateStatus,
    stackedEntries,
    stackedEntriesToMergeCount: rest.stackedEntriesToMergeCount,
    status: rest.status,
    viewerCanAdminBypassMergeRequirements,
    viewerCanEnableAutoMerge,
    viewerMergeActions: rest.viewerMergeActions,
  })

  useEffect(() => {
    if (mergeControlModel.isMergeable) {
      sendAnalyticsEvent('merge_section.view_ready_to_merge', 'MERGEBOX_MERGE_SECTION', {pullRequestId: id})
    }
  }, [mergeControlModel.isMergeable, sendAnalyticsEvent, id])

  if (mergeControlModel.visibility === 'absent') return null

  const {
    control,
    isMergeQueueEnabled,
    isMergeable,
    isBypassAllowed,
    stackPositionInStack,
    stackSize,
    visibility,
    shouldRenderBypassToggle,
  } = mergeControlModel

  let mergeComponent
  switch (control.type) {
    case 'disable-auto-merge':
      mergeComponent = (
        <DisableAutoMerge
          autoMergeRequest={autoMergeRequest}
          isMergeQueueEnabled={isMergeQueueEnabled}
          viewerCanDisableAutoMerge={viewerCanDisableAutoMerge}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        />
      )
      break
    case 'inactive-direct':
      mergeComponent = (
        <DirectMergeActionsSection
          mergeable={false}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          isAdminBypassToggleChecked={false}
          isAdminBypassToggleVisible={false}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStacked={isStacked}
          onStackMergePendingChange={onStackMergePendingChange}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          {...rest}
        />
      )
      break
    case 'merge-queue-bypass':
      mergeComponent = (
        <DirectMergeActionsSection
          mergeable={isMergeable}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          isAdminBypassToggleVisible={isBypassAllowed}
          isAdminBypassToggleChecked
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStacked={isStacked}
          onStackMergePendingChange={onStackMergePendingChange}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          {...rest}
        />
      )
      break
    case 'merge-queue':
      mergeComponent = (
        <AddToMergeQueueSection
          autoMergeRequest={autoMergeRequest}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          isInMergeQueue={isInMergeQueue}
          mergeQueue={mergeQueue}
          mergeStateStatus={mergeStateStatus}
          onStackMergePendingChange={onStackMergePendingChange}
          viewerCanAddAndRemoveFromMergeQueue={viewerCanAddAndRemoveFromMergeQueue}
          viewerCanAddToMergeQueueSolo={viewerCanAddToMergeQueueSolo}
          isStacked={isStacked}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          {...rest}
        />
      )
      break
    case 'auto-merge':
      mergeComponent = (
        <DirectMergeActionsSection
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          mergeable={isMergeable}
          isAdminBypassToggleChecked={false}
          isAdminBypassToggleVisible={isBypassAllowed}
          isAutoMergeAllowed={control.isAutoMergeAllowed}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStacked={isStacked}
          onStackMergePendingChange={onStackMergePendingChange}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          {...rest}
        />
      )
      break
    case 'direct':
      mergeComponent = (
        <DirectMergeActionsSection
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          isAdminBypassToggleChecked={isAdminBypassToggleChecked}
          isAdminBypassToggleVisible={isBypassAllowed}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          mergeable={isMergeable}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          isStacked={isStacked}
          onStackMergePendingChange={onStackMergePendingChange}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          {...rest}
        />
      )
      break
    default:
      mergeComponent = assertNeverControl(control satisfies never)
  }

  return (
    <div
      hidden={visibility === 'hidden'}
      className={clsx(
        isSidePanel ? 'tmp-pt-3 tmp-px-3' : 'bgColor-muted borderColor-muted tmp-p-3',
        isConfirmingMergeInfo ? 'rounded-2' : 'rounded-bottom-2',
      )}
    >
      {shouldRenderBypassToggle && (
        <div className="tmp-mb-3">
          <BypassMergeRequirementsToggle
            checked={isAdminBypassToggleChecked}
            onToggleChecked={() => setIsAdminBypassToggleChecked(previous => !previous)}
          />
        </div>
      )}
      {mergeComponent}
    </div>
  )
}
